import toFullName from '@/constants/toFullName';
import { getUserAvatarColor } from '@/utils/collaborationUserColor';
import { getUserInitials } from '@/utils/general';
import {
  alpha,
  Autocomplete,
  AutocompleteProps,
  Avatar,
  Box,
  Checkbox,
  Chip,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Check } from 'lucide-react';
import React from 'react';
import * as S from './styles';

import { UserLite } from '@/api';

export interface SelectUser {
  userId: number;
  user: UserLite;
  roleName?: string;
  memberId?: number;
}

export interface UserSelectProps<T extends SelectUser> extends Omit<
  AutocompleteProps<T, true, false, false>,
  'renderInput' | 'renderValue' | 'renderOption' | 'renderGroup' | 'options'
> {
  options: T[];
  placeholder?: string;
  popoverWidth?: number;
  getGroupName?: (option: T) => string;
  onToggleGroup?: (group: string, checked: boolean) => void;
  selectedIds?: number[];
  textFieldProps?: React.ComponentProps<typeof TextField>;
}

const UserSelect = <T extends SelectUser>({
  options,
  value,
  onChange,
  placeholder = 'Enter',
  getGroupName,
  onToggleGroup,
  selectedIds = [],
  textFieldProps,
  popoverWidth = 400,
  ...rest
}: UserSelectProps<T>) => {
  const { slotProps: textFieldSlotProps, ...textFieldPropsRest } = textFieldProps ?? {};

  const handleToggleGroup = (group: string, checked: boolean, e: React.ChangeEvent<unknown>) => {
    e.stopPropagation();
    onToggleGroup?.(group, checked);
  };

  return (
    <S.Container>
      <Autocomplete
        multiple
        disableCloseOnSelect
        forcePopupIcon={false}
        options={options}
        value={value}
        onChange={onChange}
        isOptionEqualToValue={(option, val) => option.userId === val.userId}
        getOptionLabel={(option) => toFullName(option.user)}
        groupBy={getGroupName}
        filterOptions={(allOptions, state) => {
          const q = state.inputValue.trim().toLowerCase();
          if (!q) return allOptions;
          return allOptions.filter((participant) => {
            const fullName = toFullName(participant.user).toLowerCase();
            const email = (participant.user.email ?? '').toLowerCase();
            const groupName = getGroupName ? getGroupName(participant).toLowerCase() : '';
            return fullName.includes(q) || email.includes(q) || groupName.includes(q);
          });
        }}
        renderValue={(selectedValues, getItemProps) =>
          selectedValues.map((member, index) => {
            const itemProps = getItemProps({ index });
            return (
              <S.RenderValueChip
                {...itemProps}
                key={member.userId}
                size='small'
                variant='action'
                avatar={
                  <Avatar color={getUserAvatarColor(member.user)}>
                    {getUserInitials(member.user)}
                  </Avatar>
                }
                label={toFullName(member.user)}
              />
            );
          })
        }
        renderGroup={(params) => {
          if (!getGroupName) return <div key={params.key}>{params.children}</div>;

          const groupParticipants = options.filter((p) => getGroupName(p) === params.group);
          const allSelected = groupParticipants.every((p) => selectedIds.includes(p.userId));

          return (
            <Box key={params.key}>
              <S.GroupHeader
                direction='row'
                alignItems='center'
                justifyContent='space-between'
                onClick={(e) => e.stopPropagation()}
              >
                <Typography variant='caption' fontWeight={700} color='grey.0' sx={{ opacity: 0.7 }}>
                  {params.group}
                </Typography>
                <Checkbox
                  size='small'
                  checked={allSelected}
                  onChange={(e) => handleToggleGroup(params.group, e.target.checked, e)}
                />
              </S.GroupHeader>
              <Divider />
              {params.children}
            </Box>
          );
        }}
        renderOption={(props, option, { selected }) => {
          const { key, ...optionProps } = props;

          return (
            <li key={key} {...optionProps}>
              <Stack direction='row' alignItems='center' spacing={1} sx={{ width: '100%' }}>
                <Avatar color={getUserAvatarColor(option.user)}>
                  {getUserInitials(option.user)}
                </Avatar>
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {toFullName(option.user)}
                </Box>
                {selected && <Box component={Check} mr={0.5} color='grey.50' size={16} />}
              </Stack>
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            {...textFieldPropsRest}
            size='small'
            name={Math.random().toString(36).slice(2)} // to prevent browser autofill
            placeholder={((value || []) as T[]).length === 0 ? placeholder : ''}
            autoComplete='off'
            slotProps={{
              ...textFieldSlotProps,
              htmlInput: {
                ...params.inputProps,
                ...(textFieldSlotProps?.htmlInput ?? {}),
                autoComplete: 'new-password',
              },
            }}
          />
        )}
        slotProps={{
          paper: {
            sx: {
              maxWidth: popoverWidth,
            },
          },
        }}
        {...rest}
      />
    </S.Container>
  );
};

export default UserSelect;
